import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ClipboardList, Printer, CalendarDays, BarChart3, Receipt, Utensils, GlassWater,
  QrCode, Settings, Users, LogOut, MoreHorizontal, X, Banknote, ShoppingBag,
  PackagePlus, type LucideIcon,
} from 'lucide-react'
import { useStaffAuth } from '../store/useStaffAuth'
import { type Store } from '../store/useStore'
import { api } from '../services/api'
import { useStoreReservations } from '../hooks/useStoreReservations'
import { useI18n } from '../i18n/useI18n'
import { canView } from '../lib/rolePolicy'
import { genreOf, usesKitchenWord } from '../lib/genre'
import { staffShowsOrders } from '../lib/staffLanding'

type TabItem = { to: string; labelKey: string; icon: LucideIcon; badge?: number }

/**
 * #995 スタッフモードの下部ナビ（POS タブレット前提・現場は素早くアクセス優先）。
 * サイドバーは lg 以上のみでタブレット縦だと消えるため、役割別の遷移を画面下に常時表示する。
 * - バイト: 店内注文 / 厨房（＋ログアウト）
 * - 店長: 店内注文 / 厨房 /（予約）＋「その他」（レポート/取引履歴/コース/店舗QR/店舗設定/
 *   スタッフ管理）＋ログアウト。項目が多いので頻用のみ主バー、残りは「その他」シートに収容。
 *
 * #1439 予約 / 売上・レポート / 取引履歴の 3 つは、オーナーが店ごとに
 * 「誰に見せるか」を決められる（`lib/rolePolicy`）。ここは UX 上の出し分けで、
 * 実際の拒否は backend の `assert_can_view` が行う。
 * オーナーの ←/🏠 でダッシュボードへ出る動線には依存させない。
 *
 * ## #1979 業態で出し分ける
 *
 * ここは長らく**飲食決め打ち**だった。店内注文と厨房が全業態で固定、
 * **レジはどこにも無く**、店長の「その他」にはコース・放題プラン（飲食の概念）が
 * 業態に関係なく並んでいた。販売の店では「店内注文を経由してレジに行く」しか
 * 手が無く、業態に合っていない。
 *
 * オーナー側 (`StoreDashboard`) は `genreOf` / `store.genre` で出し分けている。
 * **同じ考え方をここにも入れる** — 判定の根拠はオーナー側と揃える。
 *
 * 権限 (`rolePolicy` / `canView`) の判定は変えていない。足したのは業態による
 * 出し分けだけで、実際の拒否は従来どおり backend の `assert_can_view` が行う。
 */
export default function StaffTabBar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const session = useStaffAuth((s) => s.session)
  const clearSession = useStaffAuth((s) => s.clearSession)
  const storeId = session?.storeId
  const { pending } = useStoreReservations(storeId || null)
  const [moreOpen, setMoreOpen] = useState(false)

  // #2199 お店側の窓口から読む。zustand の `stores` (顧客側の一覧) はお客様に見せる
  // 項目だけなので rolePolicy が無い。
  const [apiStore, setApiStore] = useState<Store | null>(null)
  useEffect(() => {
    if (!storeId) return
    api.getManagedStore(storeId)
      .then((data: Store) => setApiStore(data))
      .catch((e: unknown) => console.error('[StaffTabBar] fetch store failed:', e))
  }, [storeId])
  const store = apiStore?.id === storeId ? apiStore : null
  const reservationEnabled = store?.reservationEnabled
  const rolePolicy = store?.rolePolicy
  const genre = store ? genreOf(store) : undefined

  const { primary, overflow } = useMemo<{ primary: TabItem[]; overflow: TabItem[] }>(() => {
    if (!storeId || !session) return { primary: [], overflow: [] }
    // #1979 **レジは全業態で出す。** 現場が一番使う画面で、会計だけの店
    // (販売・体験) にとっては**唯一のレジ導線**。以前はここに無く、店内注文の
    // 画面を経由するしか無かった。
    const cashier: TabItem = { to: `/store/cashier/${storeId}`, labelKey: 'storeDash.cashier', icon: Banknote }
    const orders: TabItem = { to: `/store/orders/${storeId}`, labelKey: 'storeNav.orders', icon: ClipboardList }
    // #1496 「厨房」は飲食の語。**隠さない** — プリンター設定はここにしかなく、
    // 販売・体験でも要る。ラベルだけ業態に合わせる。
    const kitchen: TabItem = {
      to: `/store/kitchen/${storeId}`,
      labelKey: usesKitchenWord(genre) ? 'storeNav.kitchen' : 'storeKitchen.titleNonFood',
      icon: Printer,
    }
    const reservations: TabItem = { to: `/store/reservations/${storeId}`, labelKey: 'storeNav.reservations', icon: CalendarDays, badge: pending.length }
    const report: TabItem = { to: `/store/report/${storeId}`, labelKey: 'storeNav.report', icon: BarChart3 }
    const transactions: TabItem = { to: `/store/transactions/${storeId}`, labelKey: 'storeNav.transactions', icon: Receipt }

    // #1439 オーナーが決めた「見せるもの」。予約は店の予約機能が ON のときだけ。
    const role = session.role
    const showReservations = !!reservationEnabled && canView(role, 'reservations', rolePolicy)
    // #1979 業態による出し分け (根拠はオーナー側 StoreDashboard と揃える)
    const isRetail = genre === 'souvenir'
    const isFoodLike = genre === 'food' || genre === 'other'
    // 店内注文はセルフオーダー店だけ。店頭受け取り店は同じ画面を注文管理として使う。
    // どちらでもない店 (イベントの物販ブース等) には出さない。
    //
    // **判定は共有ヘルパー。** 着地先 (StoreDashboard のリダイレクト) と別々に
    // 決めているとズレる — 実際にズレて、ナビから外した注文管理に降ろしていた。
    const showOrders = staffShowsOrders(store)
    const retailExtras: TabItem[] = isRetail ? [
      { to: `/store/settings/${storeId}?tab=products`, labelKey: 'storeDash.productManagement', icon: ShoppingBag },
      { to: `/store/stock-receive/${storeId}`, labelKey: 'stockReceive.title', icon: PackagePlus },
    ] : []
    const showSales = canView(role, 'sales', rolePolicy)
    const showTransactions = canView(role, 'transactions', rolePolicy)

    // バイト: 店内注文 / 厨房 が基本。予約・売上・取引履歴はオーナーが見せると
    // 決めたぶんだけ増える（閲覧のみ）。主バーが混まないよう 3 つ目以降は「その他」へ。
    // 主バーは**レジ + その店で実際に使う画面**。混むので 3 つまで。
    const base: TabItem[] = [cashier]
    if (showOrders) base.push(orders)
    base.push(kitchen)

    if (role === 'staff') {
      const extra: TabItem[] = [...retailExtras]
      if (showReservations) extra.push(reservations)
      if (showSales) extra.push(report)
      if (showTransactions) extra.push(transactions)
      if (extra.length === 0) return { primary: base, overflow: [] }
      if (extra.length === 1 && base.length < 3) {
        return { primary: [...base, extra[0]], overflow: [] }
      }
      return { primary: base, overflow: extra }
    }

    // 店長: 頻用（店内注文/厨房/予約）を主バー、その他は「その他」シートに。
    const overflow: TabItem[] = []
    // 予約は「その他」へ回す。**レジを主バーから押し出さない**
    if (showReservations) overflow.push(reservations)
    overflow.push(...retailExtras)
    if (showSales) overflow.push(report)
    if (showTransactions) overflow.push(transactions)
    // #1979 コース・放題プランは**飲食の概念**。オーナー側と同じく体験・販売では出さない
    if (isFoodLike) {
      if (showReservations) overflow.push({ to: `/store/courses/${storeId}`, labelKey: 'storeNav.courses', icon: Utensils })
      // #1353 放題プラン。ウォークインでも使うので予約の有無に関係なく出す。
      overflow.push({ to: `/store/all-you-can/${storeId}`, labelKey: 'storeNav.allYouCan', icon: GlassWater })
    }
    overflow.push(
      { to: `/store/qr/${storeId}`, labelKey: 'storeNav.qr', icon: QrCode },
      { to: `/store/settings/${storeId}`, labelKey: 'storeNav.settings', icon: Settings },
      { to: `/store/staff/${storeId}`, labelKey: 'storeNav.staff', icon: Users },
    )
    return { primary: base, overflow }
  }, [storeId, session, reservationEnabled, rolePolicy, pending.length, genre, store])

  if (!session || !storeId) return null

  const logout = () => {
    clearSession()
    navigate('/staff/login', { replace: true })
  }

  const go = (to: string) => {
    setMoreOpen(false)
    navigate(to)
  }

  const cell = 'relative flex flex-1 flex-col items-center justify-center gap-1 py-2 tap-effect'
  const overflowActive = overflow.some((it) => location.pathname.startsWith(it.to))

  return (
    <>
      {moreOpen && (
        <div className="fixed inset-0 z-60 flex flex-col justify-end bg-black/40" onClick={() => setMoreOpen(false)}>
          <div className="rounded-t-2xl bg-white p-4 safe-bottom" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <span className="font-display text-sm font-bold text-text-primary">{t('storeNav.more')}</span>
              <button onClick={() => setMoreOpen(false)} className="tap-effect rounded-full p-1 text-text-tertiary">
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {overflow.map((it) => {
                const Icon = it.icon
                const active = location.pathname.startsWith(it.to)
                return (
                  <button
                    key={it.to}
                    onClick={() => go(it.to)}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-4 tap-effect ${active ? 'border-yokoso-red bg-red-50' : 'border-gray-100 bg-white'}`}
                  >
                    <Icon size={24} className={active ? 'text-yokoso-red' : 'text-text-secondary'} />
                    <span className={`text-xs font-body ${active ? 'text-yokoso-red font-semibold' : 'text-text-secondary'}`}>{t(it.labelKey)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* REQ-60 z-40: 店の画面のモーダルは z-50 が多く、同じ高さだと後ろに描くこのタブが上に重なり、
        * モーダルの下端 (確定のボタン) を隠していた。モーダルより下に置く */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-100 bg-white/95 backdrop-blur-md safe-bottom">
        <div className="mx-auto flex max-w-3xl items-stretch">
          {primary.map((it) => {
            const Icon = it.icon
            const active = location.pathname.startsWith(it.to)
            return (
              <button key={it.to} onClick={() => navigate(it.to)} className={cell}>
                <Icon size={22} strokeWidth={active ? 2.2 : 1.8} className={active ? 'text-yokoso-red' : 'text-text-tertiary'} />
                <span className={`text-[11px] leading-none font-body ${active ? 'text-yokoso-red font-semibold' : 'text-text-tertiary font-medium'}`}>
                  {t(it.labelKey)}
                </span>
                {it.badge ? (
                  <span className="absolute top-1 right-[22%] flex h-4 min-w-4 items-center justify-center rounded-full bg-yokoso-red px-1 text-[10px] font-bold text-white">
                    {it.badge}
                  </span>
                ) : null}
              </button>
            )
          })}
          {overflow.length > 0 && (
            <button onClick={() => setMoreOpen(true)} className={cell}>
              <MoreHorizontal size={22} strokeWidth={overflowActive ? 2.2 : 1.8} className={overflowActive ? 'text-yokoso-red' : 'text-text-tertiary'} />
              <span className={`text-[11px] leading-none font-body ${overflowActive ? 'text-yokoso-red font-semibold' : 'text-text-tertiary font-medium'}`}>
                {t('storeNav.more')}
              </span>
            </button>
          )}
          <button onClick={logout} className={cell}>
            <LogOut size={22} strokeWidth={1.8} className="text-text-tertiary" />
            <span className="text-[11px] leading-none font-body font-medium text-text-tertiary">{t('storeStaff.logout')}</span>
          </button>
        </div>
      </nav>
    </>
  )
}
